// import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { addMonths, format, startOfMonth } from "date-fns";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import BackIcon from "@mui/icons-material/ArrowBackIosNew";
import NextIcon from "@mui/icons-material/ArrowForwardIos";
import PreviousIcon from "@mui/icons-material/ArrowBackIos";
import { useNitroStore } from "../../../../store/Store";
import GuidanceChart from "./components/chart";
import GuidanceTable from "./components/table";
import IGuidance from "../../types/IGuidance";
import GuidanceScreenshot from "./components/screenshot";
import GuidanceService from "../../lib/guidance.service";
import LoadingIcon from "../../../../components/Loading";
import cookie from "../../../../store/cookie";
import { ScreenshotIcon } from "../../../../icons";
import RetryPanel from "../../../../components/RetryPanel";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import CloseIcon from "../../../../components/CloseIcon";
import "./index.css";
import KochLocationFilter from "../../../../components/filters/KochLocationFilter";
import ProductFilter from "../../../../components/filters/ProductFilter";
import useEscapeKeyExit from "../../../../hooks/useEscapeKeyExit";

const Guidance = ({
  location,
  setLocation,
  product,
  setProduct,
  editing,
  setEditing,
  setSelectedMonth,
  onSave,
  close,
}: {
  location: string;
  setLocation: (location: string) => void;
  product: string;
  setProduct: (product: string) => void;
  editing: boolean;
  setEditing: (editing: boolean) => void;
  setSelectedMonth: (date: string) => void;
  onSave?: () => void;
  close: () => void;
}) => {
  const auth = useAuth();
  const isTablet = useIsMobile(1334);

  const [savedNotification, failedNotification] = useNitroStore((state) => [
    state.savedNotification,
    state.failedNotification,
  ]);
  useEscapeKeyExit({
    close,
    active: true,
  });

  const [currentMonth, setCurrentMonth] = useState<Date>(
    startOfMonth(new Date())
  );

  const [isLoading, setIsLoading] = useState(true);
  const [hadError, setHadError] = useState(false);
  const [originalRecords, setOriginalRecords] = useState<IGuidance[]>([]);
  const [records, setRecords] = useState<IGuidance[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  useEffect(() => {
    if (originalRecords.length === 0) return;

    const newIsDirty = originalRecords.some((r, i) => {
      const record = records[i];

      return r.fobPrice !== record.fobPrice || r.altPrice !== record.altPrice;
    });

    setIsDirty(newIsDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records]);

  const [pageSize, setPageSize] = useState(isTablet ? 3 : 6);
  useEffect(() => {
    if (isTablet) {
      setPageSize(3);
    } else {
      setPageSize(6);
    }
  }, [isTablet]);

  const loadRecords = useCallback(
    async (date: Date, l: string, p: string, pageSz: number) => {
      if (!l || !p) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setHadError(false);
      try {
        const result = await GuidanceService.getIndicationsByMonth(
          l,
          p,
          format(date, "yyyy-MM-dd"),
          format(addMonths(date, pageSz - 1), "yyyy-MM-dd")
        );

        const newRecords = new Array(pageSz).fill(0).map((_, i) => {
          const dt = format(addMonths(date, i), "yyyy-MM-dd");
          const record = result.find((r) => {
            return r.date === dt;
          });

          if (!record) {
            return {
              date: dt,
              fobPrice: 0,
              altPrice: 0,
            };
          }

          return record;
        });

        setOriginalRecords(newRecords);
        setRecords(newRecords);
      } catch (err) {
        failedNotification("Uhoh, something went wrong.", (err as any).message);
        setHadError(true);
        console.error("API Error", err);
        setOriginalRecords([]);
        setRecords([]);
      }
      setIsLoading(false);
    },
    [failedNotification]
  );

  useEffect(() => {
    if (!location || !product) {
      setRecords([]);
      setOriginalRecords([]);
      setIsLoading(false);
      return;
    }

    loadRecords(currentMonth, location, product, pageSize);
  }, [currentMonth, location, product, loadRecords, pageSize]);

  const [screenshotPanelOpen, setScreenshotPanelOpen] = useState(false);

  const save = async () => {
    setIsLoading(true);
    const userName = `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`;

    try {
      const filteredRecords = records.filter((r) => {
        const originalRecord = originalRecords.find((o) => o.date === r.date);

        if (!originalRecord) {
          return false; // Shouldn't be possible
        }

        return (
          r.fobPrice !== originalRecord.fobPrice ||
          r.altPrice !== originalRecord.altPrice
        );
      });

      await GuidanceService.createGuidance({
        records: filteredRecords,
        userName,
        currency: cookie.currency,
        uom: cookie.uom,
        product,
        location,
      });

      loadRecords(currentMonth, location, product, pageSize);
      setEditing(false);
      savedNotification("Guidance saved successfully");
      if (onSave) onSave();
    } catch (err) {
      console.error("API Error", err);

      failedNotification("Error saving guidance", (err as any).message);
    }
    setIsLoading(false);
  };

  const productRef = useRef();
  const locationRef = useRef();

  return (
    <>
      <div className="guidanceSearchBar">
        {screenshotPanelOpen && (
          <GuidanceScreenshot
            location={location}
            product={product}
            close={() => setScreenshotPanelOpen(false)}
          />
        )}

        {editing && (
          <div
            style={{
              borderRadius: isTablet ? 0 : 20,
              zIndex: 102,
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "rgba(15, 44, 82, 0.5)",
            }}
          />
        )}
        {isTablet && (
          <IconButton
            style={{
              borderRadius: 2,
              padding: "0px 14px",
              height: "100%",
            }}
            onClick={close}
          >
            <BackIcon />
          </IconButton>
        )}
        <div
          style={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
          }}
        >
          <div className="guidanceKochLocationFilter">
            <KochLocationFilter
              value={location}
              setValue={setLocation}
              isMobile={isTablet}
              config={{
                inputRef: locationRef as any,
                opaque: true,
                shadow: true,
                valueRequired: !location,
              }}
            />
          </div>
          <div className="guidanceProductFilter">
            <ProductFilter
              value={product}
              setValue={setProduct}
              isMobile={isTablet}
              config={{
                inputRef: productRef as any,
                opaque: true,
                shadow: true,
                valueRequired: !!location && !product,
              }}
            />
          </div>
        </div>
      </div>
      <div
        style={{
          flexGrow: 1,
          height: "50vh",
          overflow: "auto",
          borderRadius: isTablet ? 0 : 20,
          width: isTablet ? "100%" : 900,
          maxWidth: isTablet ? "100%" : "calc(100vw - 40px)",
          zIndex: editing ? 1 : undefined,
          backgroundColor: "#F7F7F7",
          boxShadow:
            "0px 4px 4px 0px rgba(0, 0, 0, 0.25), 0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
        }}
      >
        {editing && (
          <div
            style={{
              borderRadius: isTablet ? 0 : 20,
              zIndex: 102,
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "rgba(15, 44, 82, 0.5)",
            }}
          />
        )}

        {/* HEADER */}
        <div
          style={{
            width: "100%",
            paddingTop: 20,
            paddingBottom: 10,
            display: "flex",
            alignItems: "center",
            position: "relative",
          }}
        >
          <div className="guidanceHeader">Guidance</div>

          {!isTablet && (
            <div style={{ position: "absolute", right: 8, top: 8 }}>
              <CloseIcon onClick={close} />
            </div>
          )}
        </div>
        {/* END HEADER */}

        <div
          id="guidance-panel-body"
          style={{
            overflowX: "auto",
            overflowY: "auto",
            paddingBottom: 20,
          }}
        >
          {isLoading && (
            <div
              style={{
                height: 300,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <LoadingIcon color="#005CA6" height={100} width={100} />
            </div>
          )}

          {!isLoading && (!product || !location) && (
            <div
              style={{
                height: 300,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontStyle: "italic",
                color: "#595959",
              }}
            >
              Select a Koch location and product
            </div>
          )}
          {!isLoading && product && location && hadError && (
            <RetryPanel
              retry={() =>
                loadRecords(currentMonth, location, product, pageSize)
              }
            />
          )}
          {!isLoading && records.length > 0 && product && location && (
            <div
              style={{
                padding: "0px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 20,
              }}
            >
              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: 20 }}
              >
                <Button
                  id="guidanceScreenshotButton"
                  color="success"
                  variant="contained"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  onClick={() => setScreenshotPanelOpen(true)}
                >
                  <ScreenshotIcon color="white" />
                  SCREENSHOT
                </Button>
              </div>
              <GuidanceChart data={records} />
              <div style={{ zIndex: editing ? 106 : undefined }}>
                <GuidanceTable
                  records={records}
                  setRecords={setRecords}
                  editing={editing}
                  setEditing={setEditing}
                  selectMonth={(date) => setSelectedMonth(date)}
                />

                {!editing && (
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Tooltip title="Back 3 months">
                      <IconButton
                        onClick={() => {
                          setCurrentMonth(addMonths(currentMonth, -3));
                        }}
                      >
                        <PreviousIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Forward 3 months">
                      <IconButton
                        onClick={() => {
                          setCurrentMonth(addMonths(currentMonth, 3));
                        }}
                      >
                        <NextIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                )}

                {editing && (
                  <div
                    style={{
                      padding: 10,
                      backgroundColor: "white",
                      zIndex: editing ? 30 : undefined,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 15 }}
                    >
                      <Button
                        id="guidanceCancelButton"
                        color="error"
                        variant="contained"
                        onClick={() => {
                          setRecords(originalRecords);
                          setEditing(false);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        id="guidanceSaveButton"
                        variant="contained"
                        onClick={save}
                        disabled={!isDirty}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Guidance;
